import { useState, type ReactNode } from "react";
import { MyLoader } from "../components/loaders/SkeletonLoader";
import SearchBar from "../components/SearchBar";
import MySelectSimple from "../components/MySelectSimple";
import MyHeaderLabel from "../components/MyHeaderLabel";
import { MyTableViewJSON } from "../components/MyTableViewJSON";
import { useLang } from "../context/TranslationProvider";
import { useConfirmDialog } from "../hooks/useConfirmDialog";
import { MyInfiniteLoadMore } from "../components/MyInfiniteLoadMore";
import { myGetList } from "../hooks/myGetList";
import { useMyDialog } from "../context/MyDialogProvider";
import MyRow from "../components/MyRow";

export type DynamicRecord = Record<string, any>;

interface MyTableListProps<
  T,
  S extends DynamicRecord = DynamicRecord
> {
  endpoint: string;
  method?: string;
  queryKey: string;
  pageLabel: string;

  idKey: keyof T;
  columnsList: (keyof T)[];
  columnLabels?: Partial<Record<keyof T, string>>;
  switchColumns?: (keyof T)[];

  viewDialog?: (row: T) => ReactNode;

  onToggle?: (row: T, column: string) => void;
  onDelete?: (row: T) => void;
  getDeleteLabel?: (row: T) => string;

  categories?: string[];

  /* 🔥 Fully dynamic status config */
  statuses?: S[];
  statusValueKey?: keyof S;
  statusLabelKey?: keyof S;
  statusActiveKey?: keyof S;

  banner?: ReactNode;
  sidebar?: ReactNode;
}

export default function MyTableList<
  T,
  S extends DynamicRecord = DynamicRecord
>({
  endpoint,
  method = "GET",
  queryKey,
  pageLabel,
  idKey,
  columnsList,
  columnLabels,
  switchColumns = [],
  viewDialog,
  onToggle,
  onDelete,
  getDeleteLabel,
  categories = [],
  statuses = [],
  statusValueKey = "value" as keyof S,
  statusLabelKey = "label" as keyof S,
  statusActiveKey = "is_active" as keyof S,
  banner,
}: MyTableListProps<T, S>) {
  const { t } = useLang();
  const { open } = useMyDialog();
  const { confirm } = useConfirmDialog();

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = myGetList({
    endpoint,
    method,
    key: [queryKey, search, category, status],
    query: {
      ...(search ? { search } : {}),
      ...(category ? { category } : {}),
      ...(status ? { order_status: status } : {}),
    },
  });

  if (isLoading) return <MyLoader />;

  const items = data?.pages.flatMap((p) => p.data) ?? [];

  const isStatusActive = (s: S) => {
    const v = s[statusActiveKey];
    return v === undefined || v === true || v === 1;
  };

  return (
    <div className="p-4 sm:p-6 max-w-screen-xl mx-auto">
      <MyHeaderLabel label={pageLabel} />
      {banner}

      {/* Filters */}
      <div className="my-4 flex flex-wrap gap-2">
        <SearchBar onSearch={setSearch} />

        {categories.length > 0 && (
          <MySelectSimple
            options={categories}
            value={category}
            onChange={setCategory as any}
            placeholder={t("Select category")}
          />
        )}

        {statuses.length > 0 && (
          <MySelectSimple
            options={statuses
              .filter(isStatusActive)
              .map((s) => ({
                value: String(s[statusValueKey]),
                label: String(s[statusLabelKey]),
              }))}
            value={status}
            onChange={(val) => setStatus(val ? String(val) : null)} // ensure type matches
            //onChange={setStatus}
            placeholder={t("Select status")}
          />
        )}
      </div>

      {/* Table */}
      {isError ? (
        <div>{t("Failed to load data")}</div>
      ) : items.length === 0 ? (
        <div>{t("No records found")}</div>
      ) : (
        <MyTableViewJSON
          data={items}
          columns={columnsList}
          columnLabels={columnLabels}
          switchColumns={switchColumns}
          handleToggle={(row, col) => onToggle?.(row, col)}
          actions={[
            ...(viewDialog
              ? [
                  {
                    label: "👁",
                    onClick: (row) =>
                      open({
                        initialSnap: 0.85,
                        content: viewDialog(row),
                        header: (
                          <MyRow
                            label={`ID: ${String(row[idKey])}`}
                            value={(row as any).order_status}
                          />
                        ),
                      }),
                  },
                ]
              : []),
            ...(onDelete
              ? [
                  {
                    label: "❌",
                    className: "bg-red-200 hover:bg-red-300",
                    onClick: (row) =>
                      confirm({
                        title: t("Delete"),
                        message: `${t("Are you sure you want to delete")} "${
                          getDeleteLabel?.(row) ?? ""
                        }"?`,
                        onConfirm: () => onDelete(row),
                      }),
                  },
                ]
              : []),
          ]}
        />
      )}

      <MyInfiniteLoadMore
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
}